/// <amd-module name="Test/TestPlansHubRefresh/Pivots/AuthorPivot/Redux/AuthorReducer" />
import produce from "immer";
import * as AuthorConstants from "Test/TestPlansHubRefresh/Pivots/AuthorPivot/AuthorConstants";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions, SuitesActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { SuiteEntryTypes, TestCase } from "TestPlanCommon/Contracts/TestPlan";
import { AuthorActions, AuthorActionTypes } from "./AuthorActions";
import { ITestCaseListState } from "./AuthorContracts";


export function AuthorReducer(state: ITestCaseListState, action: AuthorActions | SuitesActions): ITestCaseListState {
    state = state || ({} as ITestCaseListState);

    return produce(state, (draftState: ITestCaseListState) => {
        switch (action.type) {
            case AuthorActionTypes.loadInitialTestCasesSucceeded:
                draftState.testCases = generateTestCaseMap(action.payload);

                draftState.columnAndSortOrder = {
                    columnId: AuthorConstants.OrderColumn,
                    sortOrder: undefined
                };
                break;

            case AuthorActionTypes.loadTestCasesForSelectedSuiteSucceeded:
                draftState.columnAndSortOrder = {
                    ...draftState.columnAndSortOrder
                };
                if (!draftState.testCases || action.payload.reload) {
                    draftState.testCases = generateTestCaseMap(action.payload.testCases);
                } else {
                    const payloadTestCaseMap = generateTestCaseMap(action.payload.testCases);
                    draftState.testCases = {
                        ...draftState.testCases,
                        ...payloadTestCaseMap
                    };
                }

                break;

            case AuthorActionTypes.unloadPreviousTestCases:
                draftState.testCases = undefined as any;
                break;

            case AuthorActionTypes.updateTestCase:
                const testCaseToUpdate = draftState.testCases[action.payload.testCaseId];
                const testCaseWorkItem = testCaseToUpdate.workItem;

                // update test case title
                testCaseWorkItem.name = action.payload.testCaseTitle; // update testCase title

                // update testCase state
                const stateIndex = testCaseWorkItem.workItemFields.findIndex((key) =>
                    key.hasOwnProperty(TestHubRefreshConstants.WorkItemField_State)
                );
                testCaseWorkItem.workItemFields[stateIndex] = { [TestHubRefreshConstants.WorkItemField_State]: action.payload.testCaseState };

                // update test case assignedTo field
                const assignedToIndex = testCaseWorkItem.workItemFields.findIndex((key) =>
                    key.hasOwnProperty(TestHubRefreshConstants.WorkItemField_AssignedTo)
                );
                if (assignedToIndex != -1) {
                    testCaseWorkItem.workItemFields[assignedToIndex] = {
                        [TestHubRefreshConstants.WorkItemField_AssignedTo]: {
                            identityRef: {
                                displayName: action.payload.testCaseAssignedTo
                            }
                        }
                    };
                } else {
                    testCaseWorkItem.workItemFields.push({
                        [TestHubRefreshConstants.WorkItemField_AssignedTo]: {
                            identityRef: {
                                displayName: action.payload.testCaseAssignedTo
                            }
                        }
                    });
                }

                if (action.payload.columnOptions) {
                    for (const columnOptionField in action.payload.columnOptions) {
                        const index = testCaseWorkItem.workItemFields.findIndex((key) => key.hasOwnProperty(columnOptionField));
                        testCaseWorkItem.workItemFields[index] = { [columnOptionField]: action.payload.columnOptions[columnOptionField] };
                    }
                }

                draftState.testCases[action.payload.testCaseId] = testCaseToUpdate;
                break;

            case AuthorActionTypes.getTestCasesFromServerSucceeded:
                for (let i = 0; i < action.payload.testCases.length; i++) {
                    const testCaseId = action.payload.testCases[i].workItem.id;
                    const testCase = action.payload.testCases[i];
                    if(draftState.testCases) {
                        draftState.testCases[testCaseId] = testCase;
                    }
                }

                break;

            case AuthorActionTypes.removeTestCasesFromSuiteSucceeded:
                const testCaseIds = action.payload.testCaseIds;
                for (let i = 0; i < testCaseIds.length; i++) {
                    delete draftState.testCases[testCaseIds[i]];
                }
                break;

            case AuthorActionTypes.updateColumnAndSortOrder:
                const { columnId, proposedSortOrder } = action.payload;
                draftState.columnAndSortOrder.columnId = columnId;
                draftState.columnAndSortOrder.sortOrder = proposedSortOrder;
                break;

            case AuthorActionTypes.reorderTestCasesSucceeded:
                for (let i = 0; i < action.payload.suiteEntries.length; i++) {
                    if (action.payload.suiteEntries[i].suiteEntryType === SuiteEntryTypes.TestCase) {
                        const testCaseId = action.payload.suiteEntries[i].id;
                        const testCase = draftState.testCases[testCaseId];
                        if (testCase) {
                            testCase.order = action.payload.suiteEntries[i].sequenceNumber;
                        }
                    }
                }
                break;

            case SuitesActionsTypes.moveTestCaseToSuiteSucceeded:
                for (let i = 0; i < action.payload.testCaseIds.length; i++) {
                    delete draftState.testCases[action.payload.testCaseIds[i]];
                }
                break;

            default:
                break;
        }
    });
}

export function generateTestCaseMap(testCases: TestCase[]): { [id: number]: TestCase } {
    const testCasesMap: { [id: number]: TestCase } = {};

    if (testCases) {
        for (const testCase of testCases) {
            testCasesMap[testCase.workItem.id] = testCase;
        }
    }

    return testCasesMap;
}

export function getTestCasesLength(testCasesMap: { [id: number]: TestCase }) {
    return Object.keys(testCasesMap).length;
}
